/**
 * P3-16: FestivalBtn —— 节日按钮（迁移自 js/village.js:4774-4832）。
 *
 * 旧实现要点：
 * - 名称/描述：i18n `village.btn.festival` / `village.btn.festival.desc`；
 * - 价格：prices=[manpower 1500, culture 5000, parchment 2500]，由基类求 enabled；
 * - visible：drama 已研究；
 * - links：x10 / x100，visible 需 carnivals perk 且 showNonApplicableButtons 或
 *   hasMultipleResources(holdQuantity)；
 * - 主点击：game.villageTab.holdFestival(1)；link 点击直调 holdFestival(holdQuantity)
 *   并手动扣资源（link handler 内部逻辑）。
 */
import { useCallback, useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { GameButton } from "../../components/GameButton";
import type { GameButtonLinkModel } from "../../components/GameButton";
import { makeModernTooltip, useButtonModel } from "../bonfire/useButtonModel";
import type { ModernButtonController } from "../bonfire/useButtonModel";

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

interface FestivalLinkSnap {
  id: string;
  title: string;
  visible: boolean;
  handler: () => void;
}

interface FestivalSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: boolean | string;
  links: FestivalLinkSnap[];
}

interface FestivalButtonControllerLike extends ModernButtonController {
  game: GamePage;
}

// ---------------------------------------------------------------------------
// 快照
// ---------------------------------------------------------------------------

function toFestivalSnapshot(model: Record<string, unknown>): FestivalSnapshot {
  const x10 = model.x10Link as FestivalLinkSnap | undefined;
  const x100 = model.x100Link as FestivalLinkSnap | undefined;
  return {
    name: model.name as string,
    description: (model.description as string) ?? "",
    visible: model.visible !== false,
    enabled: model.enabled !== false,
    resourceIsLimited: (model.resourceIsLimited as boolean | string) ?? "",
    links: [x10, x100].filter(Boolean).map((l) => l as FestivalLinkSnap),
  };
}

function festivalSnapshotEq(a: FestivalSnapshot | null, b: FestivalSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.name !== b.name ||
    a.description !== b.description ||
    a.visible !== b.visible ||
    a.enabled !== b.enabled ||
    a.resourceIsLimited !== b.resourceIsLimited ||
    a.links.length !== b.links.length
  ) {
    return false;
  }
  for (let i = 0; i < a.links.length; i++) {
    const la = a.links[i];
    const lb = b.links[i];
    if (la.id !== lb.id || la.title !== lb.title || la.visible !== lb.visible) {
      return false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

export function FestivalBtn() {
  const controller = useMemo(() => {
    const game = getGame();
    const classes = (window as unknown as Record<string, unknown>).classes as Record<string, unknown> | undefined;
    const village = classes?.village as Record<string, unknown> | undefined;
    const ui = village?.ui as Record<string, unknown> | undefined;
    const Ctor = ui?.FestivalButtonController as new (game: GamePage) => FestivalButtonControllerLike;
    if (!game || !Ctor) {
      return null;
    }
    return new Ctor(game);
  }, []);

  const createOpts = useCallback(
    (game: GamePage) => {
      const villageTab = game.villageTab as { holdFestival?: (amt: number) => void } | undefined;
      return {
        name: t("village.btn.festival"),
        description: t("village.btn.festival.desc"),
        prices: [
          { name: "manpower", val: 1500 },
          { name: "culture", val: 5000 },
          { name: "parchment", val: 2500 },
        ],
        handler: () => {
          villageTab?.holdFestival?.(1);
        },
      };
    },
    [],
  );

  const { snap, modelRef } = useButtonModel(controller, createOpts, toFestivalSnapshot, festivalSnapshotEq);

  const onClick = useCallback(() => {
    const model = modelRef.current;
    if (!model) {
      return;
    }
    const villageTab = controller?.game?.villageTab as { holdFestival?: (amt: number) => void } | undefined;
    villageTab?.holdFestival?.(1);
  }, [controller, modelRef]);

  const getTooltip = useMemo(
    () => makeModernTooltip(() => controller, createOpts),
    [controller, createOpts],
  );

  const getSelectedObject = useCallback(() => modelRef.current, [modelRef]);

  if (!snap) {
    return null;
  }

  const linkModels: GameButtonLinkModel[] = snap.links.map((link) => ({
    id: link.id,
    title: link.title,
    visible: link.visible,
    handler: () => {
      link.handler();
    },
  }));

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      visible={snap.visible}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      links={linkModels}
      getTooltip={getTooltip}
      onClick={onClick}
      getSelectedObject={getSelectedObject}
    />
  );
}
